<template>
  <div>
    <div class="card">
      <h5 class="card-header d-flex align-items-center">
        <span>Failed Emails</span>
        <button type="button" class="btn btn-sm btn-link ml-auto" :disabled="isLoading" @click="loadEmails">
          <fa v-if="isLoading" icon="circle-notch" spin />
          <span class="ml-1">Refresh</span>
        </button>
      </h5>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-sm table-striped mb-0">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Status</th>
                <th class="text-center">Attempts</th>
                <th>Error</th>
                <th>Last Update</th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isLoading && emails.length === 0">
                <td colspan="8" class="text-center text-muted py-4"><fa icon="circle-notch" spin /> Loading...</td>
              </tr>
              <tr v-else-if="emails.length === 0">
                <td colspan="8" class="text-center text-muted py-4">No failed emails.</td>
              </tr>
              <tr v-for="email in emails" :key="email.emailId">
                <td>{{ email.toAddresses }}</td>
                <td>{{ email.subject }}</td>
                <td>{{ email.code }}</td>
                <td>
                  <span class="badge" :class="statusClass(email)">{{ statusLabel(email) }}</span>
                </td>
                <td class="text-center">{{ email.attemptCount }}</td>
                <td>
                  <span v-if="email.errorResponse" class="small text-muted failed-email-error" :title="email.errorResponse">{{ email.errorResponse }}</span>
                  <span v-else class="text-muted">---</span>
                </td>
                <td>{{ formatDateTime(email.updateDate || email.lastAttemptDate || email.insertDate) }}</td>
                <td class="text-right">
                  <button type="button" class="btn btn-sm btn-outline-primary" :disabled="resendingId === email.emailId" @click="resend(email)">
                    <fa v-if="resendingId === email.emailId" icon="circle-notch" spin />
                    <span>Resend</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer d-flex align-items-center">
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page <= 1 || isLoading" @click="goToPage(page - 1)">Previous</button>
        <span class="mx-3 text-muted">Page {{ page }} of {{ totalPagesDisplay }} · {{ totalCount }} total</span>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPagesDisplay || isLoading" @click="goToPage(page + 1)">
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

const pageSize = 25;

export default {
  data() {
    return {
      emails: [],
      page: 1,
      totalCount: 0,
      totalPages: 0,
      isLoading: false,
      resendingId: null,
    };
  },
  computed: {
    totalPagesDisplay() {
      return this.totalPages || 1;
    },
  },
  created() {
    this.loadEmails();
  },
  methods: {
    loadEmails() {
      this.isLoading = true;
      return this.$axios
        .get(`/v1/admin/emails/failed?page=${this.page}&pageSize=${pageSize}`)
        .then(res => {
          this.emails = res.data.items || [];
          this.page = res.data.page || this.page;
          this.totalCount = res.data.totalCount || 0;
          this.totalPages = res.data.totalPages || 0;
        })
        .catch(error => {
          alert(`Failed to load emails: ${error.response?.data?.detail || error.message}`);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    resend(email) {
      if (!window.confirm(`Resend this email to ${email.toAddresses}?`)) return;
      this.resendingId = email.emailId;
      this.$axios
        .post(`/v1/admin/emails/${email.emailId}/resend`)
        .then(() => this.loadEmails())
        .catch(error => {
          alert(`Resend failed: ${error.response?.data?.detail || error.message}`);
        })
        .finally(() => {
          this.resendingId = null;
        });
    },
    goToPage(page) {
      this.page = page;
      this.loadEmails();
    },
    statusLabel(email) {
      if (email.webhookResult === "bounced" || email.webhookResult === "complained") return email.webhookResult;
      return email.deliveryStatus;
    },
    statusClass(email) {
      const status = this.statusLabel(email);
      return {
        "badge-danger": status === "permanently_failed" || status === "bounced",
        "badge-warning": status === "exhausted" || status === "complained",
        "badge-secondary": status !== "permanently_failed" && status !== "bounced" && status !== "exhausted" && status !== "complained",
      };
    },
    formatDateTime(value) {
      return value ? moment(value).format("YYYY-MM-DD HH:mm") : "---";
    },
  },
};
</script>

<style lang="scss" scoped>
.failed-email-error {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
